import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "../ui/sonner";
import { Check, MessageCircle, Phone, RefreshCw, Trash2, Undo2 } from "lucide-react";
import { waLinkTo } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const chime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1174.7].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      const t = ctx.currentTime + i * 0.18;
      o.frequency.value = f;
      o.type = "sine";
      g.gain.setValueAtTime(0.001, t);
      g.gain.exponentialRampToValueAtTime(0.12, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
      o.connect(g).connect(ctx.destination);
      o.start(t);
      o.stop(t + 0.6);
    });
  } catch {}
};

export default function EnquiryList({ onUnauth, onPending }) {
  const [items, setItems] = useState(null);
  const seenRef = useRef(null);

  const load = () => {
    axios
      .get(`${API}/admin/enquiries`, { withCredentials: true })
      .then((r) => {
        setItems(r.data);
        onPending?.(r.data.filter((x) => !x.handled).length);
        const ids = new Set(r.data.map((x) => x.id));
        if (seenRef.current) {
          const fresh = r.data.filter((x) => !seenRef.current.has(x.id));
          if (fresh.length > 0) {
            chime();
            toast.success(
              fresh.length === 1 ? `New enquiry from ${fresh[0].name}` : `${fresh.length} new enquiries arrived`,
              { description: "Reply on WhatsApp from the card below." }
            );
          }
        }
        seenRef.current = ids;
      })
      .catch((e) => e?.response?.status === 401 && onUnauth());
  };

  useEffect(load, []);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const setHandled = async (id, handled) => {
    await axios.patch(`${API}/admin/enquiries/${id}`, { handled }, { withCredentials: true });
    setItems((list) => list.map((x) => (x.id === id ? { ...x, handled } : x)));
    onPending?.(items.filter((x) => !(x.id === id ? handled : x.handled)).length);
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/enquiries/${id}`, { withCredentials: true });
    setItems((list) => list.filter((x) => x.id !== id));
    onPending?.(items.filter((x) => x.id !== id && !x.handled).length);
  };

  if (items === null) return <p className="py-16 text-center text-sm text-clay">Loading enquiries...</p>;

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-clay">
          {items.length} {items.length === 1 ? "enquiry" : "enquiries"} · newest first · auto-refreshes every 15s
        </p>
        <button
          data-testid="admin-refresh-button"
          onClick={load}
          className="flex items-center gap-2 rounded-full border border-gold-faint px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-gold-dark transition-colors hover:bg-gold hover:text-night"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {items.length === 0 && (
        <p data-testid="admin-enquiries-empty" className="rounded-xl border border-dashed border-gold-faint py-16 text-center text-sm text-clay">
          No enquiries yet — they will appear here the moment a customer sends one.
        </p>
      )}

      <div className="space-y-4">
        {items.map((x) => (
          <div
            key={x.id}
            data-testid={`admin-enquiry-card-${x.id}`}
            className={`rounded-xl border bg-ivory p-5 transition-all ${x.handled ? "border-parch opacity-60" : "border-gold-faint"}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <h3 className="font-serif text-xl font-semibold">{x.name}</h3>
                  <span className="rounded-full bg-gold/15 px-3 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold-dark">
                    {x.event_type}
                  </span>
                  {x.handled && (
                    <span className="rounded-full bg-night px-3 py-0.5 text-[10px] uppercase tracking-[0.2em] text-gold">
                      Handled
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-sm text-clay">
                  <a data-testid={`admin-enquiry-phone-${x.id}`} href={`tel:${x.phone}`} className="inline-flex items-center gap-1.5 hover:text-gold-dark">
                    <Phone className="h-3.5 w-3.5" /> {x.phone}
                  </a>
                  {x.event_date && <span className="ml-4">Event date: {x.event_date}</span>}
                  <span className="ml-4">{new Date(x.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                </p>
                {x.message && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-clay">“{x.message}”</p>}
              </div>
              <div className="flex gap-2">
                <a
                  data-testid={`admin-enquiry-reply-button-${x.id}`}
                  href={waLinkTo(x.phone, `Hello ${x.name}! This is Hardik from Vayu Celebrations. Thank you for your ${x.event_type} enquiry — when is a good time to talk?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Reply on WhatsApp"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-gold-faint text-gold-dark transition-colors hover:bg-gold hover:text-night"
                >
                  <MessageCircle className="h-4 w-4" />
                </a>
                <button
                  data-testid={`admin-enquiry-handled-button-${x.id}`}
                  onClick={() => setHandled(x.id, !x.handled)}
                  title={x.handled ? "Mark pending" : "Mark handled"}
                  className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${x.handled ? "border-gold-faint text-gold-dark hover:bg-gold hover:text-night" : "border-night/20 text-night hover:bg-night hover:text-gold"}`}
                >
                  {x.handled ? <Undo2 className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                </button>
                <button
                  data-testid={`admin-enquiry-delete-button-${x.id}`}
                  onClick={() => remove(x.id)}
                  title="Delete enquiry"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-red-200 text-red-500 transition-colors hover:bg-red-500 hover:text-white"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
